import { fieldValue } from "./fieldValue";
import { BOX_IMAGE_ACCEPT } from "./imageUpload";
import type { BoxImage } from "./useBoxImage";

/** The builder's first page: the box's name and an optional image. */
export function BoxNameStep({
  name,
  onName,
  image,
}: {
  name: string;
  onName: (name: string) => void;
  image: BoxImage;
}) {
  // The field's title is the text above, so the drop zone's own label is for screen readers only;
  // shown, it sits inside the grid cell and pushes the dashed box below the thumbnail's middle.
  const dropZone = (
    <s-drop-zone
      label={image.url ? "Replace box image" : "Add box image"}
      labelAccessibilityVisibility="exclusive"
      accessibilityLabel="Upload a box image: PNG, JPEG, WEBP or AVIF"
      accept={BOX_IMAGE_ACCEPT}
      disabled={image.uploading || undefined}
      error={image.error ?? undefined}
      onChange={(e) =>
        void image.pick((e.currentTarget as HTMLElement & { files?: File[] }).files?.[0])
      }
      onDropRejected={image.reject}
    />
  );

  return (
    <s-stack gap="base">
      <s-text-field
        label="Box name"
        placeholder="Rolex Mystery Box"
        details="Customers see this as the product name in your store."
        value={name}
        onInput={(e) => onName(fieldValue(e))}
      />
      <s-stack gap="small-200">
        <s-text type="strong">Box image (optional)</s-text>
        <s-text color="subdued">
          Customers see this as the product image in your store. Up to 5 MB; a square image looks
          best.
        </s-text>
        {image.url ? (
          // The thumbnail with Remove under it is about the drop zone's height, so the two line
          // up; the drop zone does not stretch to its cell.
          <s-grid gridTemplateColumns="auto 1fr" gap="base" alignItems="center">
            <s-stack gap="small-200" alignItems="center">
              <s-thumbnail
                src={image.url}
                alt={`Image of ${name.trim() || "the box"}`}
                size="large"
              />
              <s-button
                variant="tertiary"
                tone="critical"
                accessibilityLabel="Remove image"
                disabled={image.uploading || undefined}
                onClick={image.clear}
              >
                Remove
              </s-button>
            </s-stack>
            {dropZone}
          </s-grid>
        ) : (
          dropZone
        )}
        {image.uploading && (
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-spinner size="base" accessibilityLabel="Uploading" />
            <s-text color="subdued">Uploading the image…</s-text>
          </s-stack>
        )}
      </s-stack>
    </s-stack>
  );
}
